import type { Trip } from '@/app/types';

/** A trip id the backend can accept. Core's permission middleware parses
 *  trip_id as a UUID, so anything else is refused before a handler sees it —
 *  including the `local-<slug>-<hash>` ids the app's own planner mints. Any
 *  other id is a trip only this browser holds, such as the demo's. */
export function isBackendTripID(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

/** Whether the signed-in person may do what only a trip's owner can: invite,
 *  remove, share links and delete. Core says per trip, and refuses anyone else,
 *  so a trip it said nothing about is refused here too. A trip only this
 *  browser holds is theirs. */
export function canManageTrip(trip: Pick<Trip, 'id' | 'access'>): boolean {
  return trip.access === 'owner' || !isBackendTripID(trip.id);
}

/** Whether the signed-in person may change the trip's plan — the edit actions
 *  core admits for an "edit" collaborator or the owner: change or delete a stop,
 *  add or confirm a flight, capture a journal entry, and so on. The edit twin of
 *  `canManageTrip`: an editor may change the plan but not do the owner-only
 *  things (invite, remove, share links, delete).
 *
 *  Note the direction differs from `canManageTrip`. Manage gates a rare,
 *  owner-only action, so it fails CLOSED — only an explicit "owner" enables it.
 *  Editing is the default expectation for the owner, every editor and every
 *  browser-only trip, so it gates on the one signal that withdraws it: core
 *  saying "view". A backend trip whose access has not loaded yet is treated as
 *  editable rather than locking the owner out of their own trip on a data
 *  hiccup — a real view collaborator always carries access="view" (JAR-2084/
 *  JAR-2097), so they are still gated.
 *
 *  It stays this way now that every edit surface is gated (JAR-2219). Core
 *  refuses a view collaborator's write, so failing open costs a refused request.
 *  Failing closed would take an owner's own plan away over a missing field. */
export function canEditTrip(trip: Pick<Trip, 'id' | 'access'>): boolean {
  return !isBackendTripID(trip.id) || trip.access !== 'view';
}

/** One line for every read-only refusal a plan-change entry lands on: the
 *  whole-trip rebalance, the per-day "make lighter", and Jarvis's re-plan of a
 *  day alike. "changed" covers all of them (JAR-2221, JAR-2100). */
export const READ_ONLY_PLAN_NOTICE = "This trip is read-only, so its plan can't be changed.";

/** The one line shown when a re-plan is refused because Jarvis is already
 *  mid-change for this trip. One constant, so the two call sites that react to a
 *  busy re-plan can't drift (JAR-2376). */
export const JARVIS_BUSY_NOTICE = 'Jarvis is busy right now. Try that day again in a moment.';
